import { StateCreator } from 'zustand'
import { AppState, AppStore, GeneralFns, ResetGroups, StateKeys } from '@/types/store.types'
import { isValidKey, removeGuestCartId } from '@/utils/store.utils'
import { userSliceDefault } from '@/stores/user.slice'
import { sellerSliceDefault } from '@/stores/seller.slice'
import { appSliceDefault } from '@/stores/app.slice'
import { setBrowserCookie } from '@/utils/browser.utils'
import { GUEST_CART_IDS_KEY } from '@/config/cookies.config'

export const getDefaultState = (): AppState => ({
  ...userSliceDefault,
  ...sellerSliceDefault,
  ...appSliceDefault,
  _hasHydrated: true,
})

export const createGeneralSlice: StateCreator<AppStore, [], [], GeneralFns> = (set) => ({
  updatePartial: (partial: Partial<AppState>) => set((state) => {
    const nextState: Partial<AppState> = { ...partial }

    // complete is set in useCheckout
    if (partial.complete) {
      setBrowserCookie(GUEST_CART_IDS_KEY, '', { minutesToExpire: -1 })
    }

    return {
      ...state,
      ...nextState,
    }
  }),
  /**
   * Resets all state except for preserved state.
   * Preserve loading blob state
   */
  resetAll: () => set((state) => {
    removeGuestCartId()
    const {
      toggleLoadingBlob,
      loadingBlob,
    } = state

    return {
      ...getDefaultState(),
      toggleLoadingBlob,
      loadingBlob,
    }
  }),
  resetPartial: () => set((state) => {
    // Preserved state
    const {
      admissionType,
      formValues,
    } = state

    removeGuestCartId()

    return {
      ...getDefaultState(),
      admissionType,
      formValues,
    }
  }),
  resetByGroups: (groups: ResetGroups[]) => set((state) => {
    const nextState = { ...state }
    const defaultState = getDefaultState()
    let keys: StateKeys[] = []

    if (groups.includes('cart') && groups.includes('localItems')) {
      // eslint-disable-next-line no-console
      console.log('%c Redundant reset of cart and local items', 'color: red;')
    }

    if ((groups.includes('time') || groups.includes('date')) && groups.includes('dateAndTime')) {
      // eslint-disable-next-line no-console
      console.log('%c Redundant reset of time or date and dateAndTime', 'color: red;')
    }

    if (groups.includes('cart')) {
      keys = [
        ...keys,
        'cartId',
        'appliedGiftCards',
        'paymentMethod',
        'lastStepCompleted',
      ]
      removeGuestCartId()
    }

    if (groups.includes('localItems') || groups.includes('cart')) {
      keys = [
        ...keys,
        'localLineItems',
        'lineItemsToDelete',
        'additionalInfoResponses',
      ]
    }

    if (groups.includes('date') || groups.includes('dateAndTime')) {
      keys = [
        ...keys,
        'activeDate',
      ]
    }

    if (groups.includes('time') || groups.includes('dateAndTime')) {
      keys = [
        ...keys,
        'activeStartTime',
      ]
    }

    keys.forEach((key) => {
      if (isValidKey(key, defaultState)) {
        // @ts-expect-error does not like this for some reason
        nextState[key] = defaultState[key]
      } else {
        // eslint-disable-next-line no-console
        console.log(`%cKey ${key} is not valid`, 'color: red;')
      }
    })

    return nextState
  }),
  resetByKeys: (keys: StateKeys[]) => set((state) => {
    const nextState = { ...state }
    const defaultState = getDefaultState()
    keys.forEach((key) => {
      if (isValidKey(key, defaultState)) {
        if (key === 'cartId') {
          removeGuestCartId()
        }
        // @ts-expect-error does not like this for some reason
        nextState[key] = defaultState[key]
      } else {
        // eslint-disable-next-line no-console
        console.log(`%cKey ${key} is not valid`, 'color: red;')
      }
    })
    return nextState
  }),
})
